import React, { useEffect, useMemo, useState } from "react";
import { weekDays } from "../../utils/weekUtils";
import { getTimeEntries, submitWeek, approveWeek, rejectWeek } from "../../api";
import StatusChip from "../StatusChip";
import { TE_STYLES } from "./teStyles";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function shiftDays(iso, delta) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${mm}-${dd}`;
}

function dayNum(iso) {
  return Number(iso.split("-")[2]);
}

// Calendar month: one row per ISO week (Mon–Sun), a scrollable cell per day,
// a week-hours Total, and per-week actions (review: Approve / Send back).
export default function CalendarMonth({ weekStarts, createdById, clients, timeCodes, review }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const rangeStart = weekStarts[0];
  const rangeEnd = shiftDays(weekStarts[weekStarts.length - 1], 6);

  const load = async () => {
    setLoading(true);
    const data = await getTimeEntries({
      created_by: createdById, start_date: rangeStart, end_date: rangeEnd,
    });
    setEntries(data);
    setLoading(false);
  };
  useEffect(() => { if (createdById) load(); /* eslint-disable-next-line */ },
    [createdById, rangeStart, rangeEnd]);

  const clientName = (id) => clients.find((c) => String(c.id) === String(id))?.name || "—";
  const byDay = useMemo(() => {
    const m = {};
    entries.forEach((e) => { (m[e.date] = m[e.date] || []).push(e); });
    return m;
  }, [entries]);

  const onApprove = async (ws) => { await approveWeek(ws, createdById); load(); };
  const onSendBack = async (ws) => {
    const note = window.prompt("Reason to send this week back to the employee:");
    if (note === null) return;
    if (!note.trim()) {
      window.alert("A reason is required to send time back.");
      return;
    }
    await rejectWeek(ws, createdById, note.trim());
    load();
  };
  const onSubmit = async (ws) => {
    try {
      await submitWeek(ws, createdById);
      load();
    } catch (e) {
      window.alert("Some lines are incomplete — open the week to fix them before submitting.");
    }
  };

  if (loading) return <div className="te-loading"><style>{TE_STYLES}</style>Loading month…</div>;

  return (
    <div className="te-wrap">
      <style>{TE_STYLES}</style>
      <div className="te-frame">
        <table className="cal">
          <colgroup>
            {DOW.map((d) => <col key={d} />)}
            <col style={{ width: 96 }} />
            <col style={{ width: 210 }} />
          </colgroup>
          <thead>
            <tr>
              {DOW.map((d) => <th key={d}>{d}</th>)}
              <th className="cal-num">Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {weekStarts.map((ws) => {
              const days = weekDays(ws);
              const weekEntries = days.flatMap((d) => byDay[d] || []);
              const weekHours = weekEntries.reduce((s, e) => s + (Number(e.hours) || 0), 0);
              const persisted = weekEntries.filter((e) => e.id);
              const hasSubmitted = persisted.some((e) => e.status === "submitted");
              const canSubmit = persisted.some((e) => e.status === "draft" || e.status === "rejected");
              const weekStatus = persisted.length
                ? (persisted.every((e) => e.status === "approved") ? "approved"
                  : persisted.some((e) => e.status === "submitted") ? "submitted"
                  : persisted.some((e) => e.status === "rejected") ? "rejected" : "draft")
                : "draft";
              return (
                <tr key={ws}>
                  {days.map((d) => {
                    const dayEntries = byDay[d] || [];
                    const dayHrs = dayEntries.reduce((s, e) => s + (Number(e.hours) || 0), 0);
                    return (
                      <td key={d}>
                        <div className="cal-dayhdr">
                          <span className="cal-date">{dayNum(d)}</span>
                          {dayHrs > 0 && <span className="cal-dayttl">{dayHrs}h</span>}
                        </div>
                        <div className="cal-daybox">
                          {dayEntries.map((e, i) => (
                            <span key={e.id ?? `${d}-${i}`}
                              className={`cal-chip s-${e.status || "draft"}`}
                              title={`${clientName(e.client)} — ${Number(e.hours) || 0}h${e.notes ? " — " + e.notes : ""}`}
                              onClick={() => setDetail(e)}>
                              {clientName(e.client)} · {Number(e.hours) || 0}h
                            </span>
                          ))}
                        </div>
                      </td>
                    );
                  })}
                  <td className="cal-total">{weekHours.toFixed(2)}h</td>
                  <td className="cal-actions">
                    {persisted.length === 0 ? null : review ? (
                      <div className="cal-act">
                        <button className="cal-btn cal-btn-approve" disabled={!hasSubmitted}
                          onClick={() => onApprove(ws)}>Approve</button>
                        <button className="cal-btn cal-btn-sb"
                          onClick={() => onSendBack(ws)}>Send back</button>
                      </div>
                    ) : (
                      <div className="cal-act">
                        <StatusChip status={weekStatus} />
                        {canSubmit && (
                          <button className="cal-btn cal-btn-submit"
                            onClick={() => onSubmit(ws)}>Submit</button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="te-modal-overlay" onClick={() => setDetail(null)}>
          <div className="te-modal" onClick={(e) => e.stopPropagation()}>
            <div className="te-modal-head">
              <h3>{clientName(detail.client)}</h3>
              <button className="te-modal-x" onClick={() => setDetail(null)}>✕</button>
            </div>
            <div className="te-modal-row"><span className="te-modal-label">Date</span><span className="te-modal-val">{detail.date}</span></div>
            <div className="te-modal-row"><span className="te-modal-label">Code</span><span className="te-modal-val">{detail.time_code || "—"}</span></div>
            <div className="te-modal-row"><span className="te-modal-label">Type</span><span className="te-modal-val">{detail.billable_code === "NON_BILLABLE" ? "Non-billable" : "Billable"}</span></div>
            <div className="te-modal-row"><span className="te-modal-label">Hours</span><span className="te-modal-val">{Number(detail.hours) || 0}</span></div>
            <div className="te-modal-row"><span className="te-modal-label">Year End</span><span className="te-modal-val">{detail.year_end || "—"}</span></div>
            <div className="te-modal-row"><span className="te-modal-label">Status</span><span className="te-modal-val"><StatusChip status={detail.status} note={detail.review_note} /></span></div>
            <div className="te-modal-notes">{detail.notes ? detail.notes : <span style={{ color: "#9aa0a6" }}>No notes</span>}</div>
          </div>
        </div>
      )}
    </div>
  );
}
